import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { CrossIcon, CrosshairIcon, ShieldCrossIcon, DoveIcon } from "@/components/Icons";
import { LogoMark } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1522869870356-127b04d38aa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx0YWN0aWNhbCUyMGFwcGFyZWwlMjBqYWNrZXQlMjBvdXRkb29yJTIwcmFuZ2UlMjBzaG9vdGVyfGVufDB8fHx8MTc5MTIyNzAwOXww&ixlib=rb-4.1.0&q=85";
const HERITAGE_IMG = "https://images.unsplash.com/photo-1674953477961-d46dd2dfd58f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwY3Jvc3MlMjBsZWF0aGVyJTIwam91cm5hbCUyMGNvZmZlZSUyMGJvb2slMjByYW5nZSUyMGdlYXJ8ZW58MHx8fHwxNzkxMjI3MDA5fDA&ixlib=rb-4.1.0&q=85";
const RANGE_IMG = "https://images.unsplash.com/photo-1533091917107-98f819e4cb54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzaG9vdGluZyUyMHJhbmdlJTIwb3V0ZG9vciUyMHJpZmxlJTIwdHJhaW5pbmclMjBncm91cCUyMHNxdWFkfGVufDB8fHx8MTc5MTIyNzAxNnww&ixlib=rb-4.1.0&q=85";
const PRECISION_IMG = "https://images.unsplash.com/photo-1612473249226-8317a036f89d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHx0YWN0aWNhbCUyMGFwcGFyZWwlMjBqYWNrZXQlMjBvdXRkb29yJTIwcmFuZ2UlMjBzaG9vdGVyfGVufDB8fHx8MTc5MTIyNzAwOXww&ixlib=rb-4.1.0&q=85";

const MARQUEE = ["FELLOWSHIP", "MARKSMANSHIP", "MORAL STRENGTH", "SACRED DUTY", "HONOR & INTEGRITY", "PSALM 144:1"];

const PILLARS = [
  { icon: CrossIcon, title: "Faith Foundations", text: "Every gathering opens in the Word. We believe skill at arms is a stewardship given by God, governed by Scripture.", span: "md:col-span-7 md:row-span-2", img: HERITAGE_IMG },
  { icon: CrosshairIcon, title: "Tactical Mastery", text: "Structured courses from first shots to long-range precision, taught by certified instructors.", span: "md:col-span-5", img: PRECISION_IMG },
  { icon: ShieldCrossIcon, title: "Community Outreach", text: "Church security teams, food drives, and disaster readiness for our neighbors.", span: "md:col-span-5", img: null },
  { icon: DoveIcon, title: "Youth & Family Safety", text: "Eddie Eagle youth training and family safety days, because protection begins at home.", span: "md:col-span-12", img: RANGE_IMG },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden noise-overlay crosshair-grid" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-10 pt-24 w-full">
        <motion.div
          className="relative w-fit mb-10"
          initial={{ opacity: 0, scale: 0.82, filter: "blur(16px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-emblem"
        >
          <motion.div
            className="absolute -inset-12 rounded-full bg-gold/15 blur-3xl"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 3, delay: 0.4, ease: "easeOut" }}
          />
          <motion.div
            className="absolute -inset-12 rounded-full bg-crimson/10 blur-3xl"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1.15 }}
            transition={{ duration: 3.4, delay: 0.7, ease: "easeOut" }}
          />
          <LogoMark className="relative w-36 sm:w-44 lg:w-52 drop-shadow-[0_0_35px_rgba(212,175,55,0.4)] transition-transform duration-500 hover:scale-105" />
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }} className="eyebrow mb-6" data-testid="hero-eyebrow">
          Est. MMXXVI &mdash; A Fellowship of Armed Believers
        </motion.p>
        <MaskedLines
          baseDelay={1.25}
          lines={["FAITH IN PURPOSE.", "PRECISION IN ACTION."]}
          lineClassName="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] text-parchment"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.8 }}
          className="mt-7 max-w-xl font-serif text-lg sm:text-xl text-stone-300 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Crusaders of the Cross is a faith-based firearms club where Christian men and women train
          with discipline, fellowship, and a clear conscience.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
          <Link to="/join" data-testid="hero-join-cta" className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold px-8 py-4 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300">
            Join the Order
          </Link>
          <Link to="/store" data-testid="hero-store-cta" className="border border-crimson/60 text-parchment font-condensed uppercase tracking-[0.22em] px-8 py-4 hover:border-crimson hover:text-crimson hover:shadow-[0_0_30px_rgba(178,34,34,0.35)] hover:-translate-y-0.5 transition-[border-color,color,box-shadow,transform] duration-300">
            Enter the Armory
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 right-8 hidden lg:block font-mono text-[10px] tracking-[0.3em] text-gold/50 uppercase"
      >
        34.0522° N — 118.2437° W
      </motion.div>
    </section>
  );
}

function Marquee() {
  const row = [...MARQUEE, ...MARQUEE, ...MARQUEE];
  return (
    <div className="border-y border-gold/15 bg-[#08090C] py-5 overflow-hidden" data-testid="editorial-marquee" aria-hidden="true">
      <div className="flex w-max animate-marquee gap-0">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0">
            {row.map((word, i) => (
              <span key={`${half}-${i}`} className={`flex items-center gap-8 px-8 font-display text-sm sm:text-base tracking-[0.35em] uppercase whitespace-nowrap ${i % 2 ? "text-crimson/80" : "text-gold/70"}`}>
                {word}
                <LogoMark className={`w-4 h-4 ${i % 2 ? "text-crimson/50" : "text-gold/40"}`} />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Pillars() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="pillars-section">
      <Reveal>
        <p className="eyebrow mb-4">The Four Pillars</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-parchment uppercase tracking-wide max-w-2xl leading-snug">
          An order built on <span className="text-gold">Scripture</span>, sharpened by <span className="text-gold">discipline</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1} className={p.span}>
            <div className="group relative h-full min-h-[240px] border border-gold/15 bg-card2/50 overflow-hidden noise-overlay hover:border-crimson/40 transition-colors duration-500" data-testid={`pillar-card-${i}`}>
              {p.img && (
                <img src={p.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-700" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
              <div className="relative p-7 h-full flex flex-col justify-end">
                <div className="w-14 h-14 border border-gold/30 bg-obsidian/70 flex items-center justify-center mb-4 group-hover:border-gold group-hover:shadow-[0_0_22px_rgba(212,175,55,0.3)] group-hover:-translate-y-0.5 transition-[border-color,box-shadow,transform] duration-300">
                  <p.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl uppercase tracking-wider text-parchment">{p.title}</h3>
                <p className="mt-3 text-sm text-stone-300 leading-relaxed max-w-md">{p.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function StoreSpotlight() {
  const [products, setProducts] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const featured = data.filter((p) => p.featured);
      const filled = featured.length >= 4 ? featured : [...featured, ...data.filter((p) => !p.featured)];
      setProducts(filled.slice(0, 4));
    }).catch(() => {});
  }, []);

  if (!products.length) return null;
  return (
    <section className="border-t border-gold/10 bg-[#0D0F16]" data-testid="store-spotlight-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow mb-4">The Armory</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-parchment uppercase tracking-wide">Gear worthy of the calling</h2>
          </div>
          <Link to="/store" data-testid="spotlight-view-all-link" className="group flex items-center gap-2 font-condensed uppercase tracking-[0.2em] text-sm text-gold hover:text-gold-light transition-colors">
            Full Store <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div className="group border border-gold/15 bg-card2/60 overflow-hidden hover:border-crimson/40 transition-colors duration-500" data-testid={`spotlight-product-${p.id}`}>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
                  <div className="absolute inset-3 border border-gold/0 group-hover:border-gold/40 transition-colors duration-500 pointer-events-none" />
                </div>
                <div className="p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{p.category}</div>
                  <h3 className="font-condensed uppercase tracking-wider text-lg text-parchment mt-1">{p.name}</h3>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-mono text-gold">${p.price.toFixed(2)}</span>
                    <button
                      data-testid={`spotlight-add-${p.id}`}
                      onClick={() => { add(p); toast.success(`${p.name} added to your kit`); }}
                      className="font-condensed uppercase tracking-widest text-xs border border-gold/40 px-3.5 py-2 hover:bg-gold hover:text-obsidian transition-colors duration-300"
                    >
                      Add to Kit
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventsTeaser() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="events-teaser-section">
      <Reveal>
        <p className="eyebrow mb-4">On the Calendar</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-parchment uppercase tracking-wide">Upcoming musters</h2>
      </Reveal>
      <div className="mt-12 space-y-px">
        {events.map((e, i) => (
          <Reveal key={e.id} delay={i * 0.08}>
            <div className="group grid sm:grid-cols-12 gap-4 items-center border border-gold/15 bg-card2/40 hover:bg-card2/80 px-6 py-6 transition-colors duration-300" data-testid={`teaser-event-${e.id}`}>
              <div className="sm:col-span-2 font-mono text-gold text-sm tracking-widest">
                {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "2-digit" }).toUpperCase()}
              </div>
              <div className="sm:col-span-6">
                <h3 className="font-condensed uppercase tracking-wider text-xl text-parchment">{e.title}</h3>
                <p className="text-sm text-stone-400 mt-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gold/70" />{e.location}</p>
              </div>
              <div className="sm:col-span-2 font-mono text-xs text-stone-400 tracking-widest uppercase flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gold/70" />{e.type}
              </div>
              <div className="sm:col-span-2 sm:text-right">
                <Link to="/members" data-testid={`teaser-event-link-${e.id}`} className="inline-block font-condensed uppercase tracking-widest text-xs border border-gold/40 px-4 py-2 hover:bg-gold hover:text-obsidian transition-colors duration-300">
                  RSVP
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CreedBand() {
  return (
    <section className="relative border-y border-gold/15 overflow-hidden" data-testid="creed-section">
      <img src={RANGE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-obsidian/70" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-28 text-center">
        <Reveal>
          <LogoMark className="w-10 h-10 text-gold mx-auto mb-8" />
          <p className="font-serif italic text-2xl sm:text-3xl text-parchment leading-relaxed">
            &ldquo;But now if you have a purse, take it, and also a bag; and if you don&rsquo;t have a sword,
            sell your cloak and buy one.&rdquo;
          </p>
          <p className="mt-6 font-mono text-xs tracking-[0.35em] text-crimson uppercase">— Luke 22:36</p>
        </Reveal>
      </div>
    </section>
  );
}

function DonateBand() {
  return (
    <section className="relative border-y border-crimson/25 bg-[#120B0D] overflow-hidden" data-testid="donate-band-section">
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-crimson/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="eyebrow mb-4">Sponsorship &amp; Giving</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide text-parchment leading-snug">
                Not called to join? You can still <span className="text-gold">fuel the mission</span>
              </h2>
              <p className="mt-5 text-stone-300 text-sm sm:text-base leading-relaxed max-w-xl">
                Friends of the order keep our ranges safe, our youth trained, and our benevolence fund ready.
                Give once or monthly — every dollar is designated to the cause you choose.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                <span><span className="text-gold">$25</span> Friend of the Order</span>
                <span><span className="text-gold">$100</span> Patron</span>
                <span><span className="text-gold">$500</span> Benefactor</span>
              </div>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/donate" data-testid="cta-donate-button" className="inline-block border-2 border-crimson bg-crimson/10 text-parchment font-condensed uppercase tracking-[0.22em] font-semibold px-10 py-4 hover:bg-crimson hover:shadow-[0_0_30px_rgba(178,34,34,0.4)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300">
                Give Today
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function JoinCta() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="join-cta-section">
      <Reveal>
        <div className="relative border border-gold/30 bg-card2/60 noise-overlay px-8 sm:px-14 py-16 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-crimson/15 blur-3xl pointer-events-none" />
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="eyebrow mb-4">Membership</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide text-parchment leading-snug">
                Stand with brothers and sisters who train as they believe
              </h2>
              <p className="mt-5 text-stone-300 text-sm sm:text-base leading-relaxed max-w-xl">
                Range access, certified instruction, member pricing in the armory, and a fellowship that prays
                as hard as it trains. Applications reviewed within one week.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/join" data-testid="cta-join-button" className="inline-block gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold px-10 py-4 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300">
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Counter({ to, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / 1400, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <div ref={ref} className="text-center group cursor-default">
      <div className="font-display text-4xl sm:text-5xl text-gold group-hover:text-gold-light transition-colors duration-300" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{val}</div>
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-2">{label}</div>
    </div>
  );
}

function StatsBand() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return null;
  const items = [
    { to: stats.members, label: "Members in the Order" },
    { to: stats.events, label: "Musters on the Calendar" },
    { to: stats.devotions, label: "Devotions Published" },
    { to: stats.resources, label: "Training Resources" },
  ];
  return (
    <section className="border-y border-gold/10 bg-[#0D0F16]" data-testid="stats-band">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {items.map((s) => <Counter key={s.label} {...s} />)}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Pillars />
      <StatsBand />
      <StoreSpotlight />
      <CreedBand />
      <EventsTeaser />
      <DonateBand />
      <JoinCta />
    </main>
  );
}
